const { fetchJSON } = window.BAO.Utils

class ProductColour {
  /**
   * @param {{
   *   handle: string,
   *   featuredImage: {
   *     url: string,
   *   },
   *   hex: {
   *     value: string,
   *   },
   *   swatchImage: {
   *     reference: {
   *       image: {
   *         url: string,
   *       }
   *     },
   *   } | null,
   *   title: {
   *     value: string,
   *   }
   * }} data
   * @param {ProductColours} colours
   */
  constructor (data, colours) {
    this.data = data
    this.colours = colours
  }

  render () {
    const clone = this.colours.template.cloneNode(true)

    const anchor = clone.querySelector('a')
    anchor.setAttribute(
      'href',
      `${window.routes.rootUrl}products/${this.handle}`,
    )
    anchor.setAttribute(
      'aria-current',
      (this.handle === this.colours.currentHandle).toString(),
    )
    anchor.setAttribute('data-title', this.title)

    const dot = clone.querySelector(`[data-dot]`)
    dot.style.setProperty('--Colour', this.hex)

    if (this.data.swatchImage) {
      dot.style.setProperty(
        '--Background_Image',
        `url(${this.data.swatchImage.reference.image.url})`,
      )
    }

    const span = dot.querySelector('span')
    span.textContent = this.title

    const tooltip = clone.querySelector(`[data-tooltip]`)
    tooltip.appendChild(this.renderImage())

    this.colours.appendChild(clone)
  }

  renderImage () {
    const width = 120
    const height = 165

    const div = window.BAO.Utils.createElement('div', {
      class: 'rsp-Image',
      style: `--Aspect_Ratio: ${width} / ${height}`,
    })
    const image = window.BAO.Utils.createElement('img', {
      class: 'rsp-Image_Image',
      src: this.data.featuredImage.url,
      width,
      height,
      alt: this.title,
      sizes: `(max-width: 767px) 100vw, (min-width: 768px) calc(100vw / 3)`,
    })

    div.appendChild(image)

    return div
  }

  get handle () {
    return this.data.handle
  }

  get hex () {
    return this.data.hex?.value
  }

  get title () {
    return this.data.title.value
  }
}

class ProductColours extends window.BAO.CustomElement() {
  async connectedCallback () {
    super.connectedCallback()

    this.data = []
    this.data = await this.fetchColours()

    this.renderColours()

    this.onMouseOver = this.onMouseOver.bind(this)
    this.onMouseOut = this.onMouseOut.bind(this)

    this.listeners.add(this, 'mouseover', this.onMouseOver)
    this.listeners.add(this, 'mouseout', this.onMouseOut)
  }

  checkValidity () {
    super.checkValidity()

    if (!this.hasAttribute('data-template-id')) {
      this.throwError(
        'Please provide a template ID for the template to render colours',
      )
    }

    if (this.productHandles.length === 0) {
      this.throwError('Please provide at least one product handle')
    }
  }

  async fetchColours () {
    try {
      const { data } = await fetchJSON(`/api/2022-07/graphql.json`, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/json;',
          'X-Shopify-Storefront-Access-Token':
            window.theme.storefrontAccessToken,
        },
        body: JSON.stringify({
          query: this.getProductsQuery(),
        }),
      })

      return Object.keys(data)
        .filter(key => data[key]?.availableForSale)
        .map(key => new ProductColour(data[key], this))
    } catch (e) {
      console.error(await e.json())
    }
  }

  renderColours () {
    if (this.data.length === 0) {
      return this.clearColours()
    }

    this.clearLoadingColours()
    this.data.forEach(colour => colour.render())
  }

  clearColours () {
    this.remove()
  }

  clearLoadingColours () {
    this.els.loading.elements.forEach(el => el.remove())
  }

  onMouseOver (event) {
    const titleEl = event.target.closest(`[data-title]`)
    if (!titleEl) {
      return
    }
    this.els.title.element.textContent = titleEl.dataset.title
  }

  onMouseOut (event) {
    this.els.title.element.textContent = this.currentColour.title
  }

  getProductsQuery () {
    /*
     * @note: If this returns null, ensure that the product is published
     * to the 'Storefront colours' channel.
     */
    return `query {
      ${this.productHandles
        .map(
          (handle, index) => `
        product${index}: product (handle: "${handle}") {
          availableForSale
          handle
          featuredImage {
            url (transform: { maxWidth: 120 })
          }
          swatchImage: metafield (namespace: "${this.swatchNamespace}", key: "${this.swatchKey}") {
            reference {
              ... on GenericFile {
                previewImage {
                  url (transform: { maxWidth: 48 })
                }
              }
              ... on MediaImage {
                image {
                  url (transform: { maxWidth: 48 })
                }
              }
            }
          }
          hex: metafield (namespace: "colours", key: "hex") {
            value
          }
          title: metafield (namespace: "colours", key: "title") {
            value
          }
        }
      `,
        )
        .join('')}
    }`
  }

  get isJp () {
    return window.theme.site === 'sunspel-jp.myshopify.com'
  }

  get swatchNamespace () {
    return this.isJp ? 'custom' : 'colours'
  }

  get swatchKey () {
    return this.isJp ? '_plp_pdp_swatch_image' : 'image'
  }

  get currentColour () {
    return this.data.find(colour => colour.handle === this.currentHandle)
  }

  get currentHandle () {
    return this.dataset.currentHandle
  }

  get productHandles () {
    return this.dataset?.handles.split('|') ?? []
  }

  get template () {
    const templateNode = document.getElementById(
      this.getAttribute('data-template-id'),
    )

    return templateNode.content
  }
}

if (!customElements.get('product-colours')) {
  customElements.define('product-colours', ProductColours)
}
